/**
 * Component: Widget Error State Rendering
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R08, WIDGET-V1-R19, WIDGET-V1-R20
 *
 * Render a documented error card inside the widget
 * container when initialisation or a surface call
 * encounters a non-recoverable failure. Purpose:
 *
 *   - R20 ("graceful degradation on backend unavailability
 *     by displaying a documented error state") — when the
 *     backend is unreachable, the widget must not blank
 *     out or show a blank container; a documented error
 *     card tells the clinician what happened and whom to
 *     contact.
 *   - R08 / R19 — auth failures and rate limits ALSO
 *     render error cards with their own copy; the single
 *     renderer here handles all three cases so the visual
 *     shape is consistent.
 *
 * The renderer is deliberately framework-free (plain DOM):
 *
 *   - Batch A UMD bundles ship without React on the critical
 *     error path so a failure BEFORE React has booted still
 *     renders a visible error card.
 *   - jsdom unit tests can exercise the renderer without
 *     spinning up a full React tree.
 *
 * R22 CSP compatibility: styles are applied as a single
 * inline style attribute on the wrapper element (not per
 * node), so hosts running ``style-src 'self' 'unsafe-inline'``
 * (the vast majority, because their own CSS-in-JS needs
 * the same allowance) embed the widget's error card
 * without relaxing their policy.
 */

import {
  WidgetAuthError,
  WidgetNetworkError,
  WidgetRateLimitError,
} from '../api/errors';

/**
 * Map the documented error types to user-visible copy.
 * Authors tweaking the copy here should update
 * ``widget.md`` §4.8 in lockstep so the integration guide
 * continues to show the exact text clinicians will see.
 *
 * Unrecognised error types fall back to a generic
 * "something went wrong" message so the renderer never
 * silently shows nothing.
 */
function resolveErrorCopy(error: Error): {
  title: string;
  body: string;
} {
  if (error instanceof WidgetRateLimitError) {
    const retryHint =
      error.retryAfterSeconds !== null
        ? ` Please try again in about ${error.retryAfterSeconds} seconds.`
        : ' Please try again in a few minutes.';
    return {
      title: 'Rate limit reached',
      body:
        'The Galen deployment is temporarily limiting the number of requests ' +
        'from this widget.' +
        retryHint,
    };
  }

  if (error instanceof WidgetAuthError) {
    return {
      title: 'Authentication required',
      body:
        'The widget could not authenticate with this Galen deployment. ' +
        'Please refresh the page; if the problem persists, contact your ' +
        'administrator.',
    };
  }

  if (error instanceof WidgetNetworkError) {
    return {
      title: 'Galen is temporarily unavailable',
      body:
        'The widget cannot reach the Galen deployment right now. Please check ' +
        'your network connection and try again in a few moments. If the problem ' +
        'persists, contact your administrator.',
    };
  }

  return {
    title: 'Something went wrong',
    body:
      'The widget encountered an unexpected error and cannot render. ' +
      'Please refresh the page; if the problem persists, contact your ' +
      'administrator.',
  };
}

/**
 * Render (or re-render) the documented error card into
 * ``container``. Replaces any existing content so a
 * partial surface render is cleared before the error
 * message is shown.
 *
 * The card carries data attributes the host page can
 * query in automated tests:
 *
 *   data-galen-error-state="true"
 *   data-galen-error-kind="<error.name>"
 *
 * which lets host integration tests assert "the widget
 * shows an error state" without scraping the visible
 * copy.
 */
export function renderErrorState(container: HTMLElement, error: Error): void {
  // Clear the container so any partially-rendered surface
  // does not show alongside the error card.
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }

  const { title, body } = resolveErrorCopy(error);
  const wrapper = container.ownerDocument.createElement('div');
  wrapper.setAttribute('data-galen-error-state', 'true');
  wrapper.setAttribute('data-galen-error-kind', error.name || 'Error');
  wrapper.setAttribute('role', 'alert');
  wrapper.style.cssText =
    'padding: 1rem; border: 1px solid var(--galen-error-border, #fca5a5); ' +
    'background: var(--galen-error-bg, #fef2f2); ' +
    'color: var(--galen-error-fg, #991b1b); ' +
    'border-radius: var(--galen-border-radius, 0.5rem); ' +
    'font-family: var(--galen-font-family, system-ui, sans-serif); ' +
    'font-size: 0.9rem; line-height: 1.5;';

  const heading = container.ownerDocument.createElement('strong');
  heading.textContent = title;
  heading.style.display = 'block';
  heading.style.marginBottom = '0.5rem';

  const message = container.ownerDocument.createElement('span');
  message.textContent = body;

  wrapper.appendChild(heading);
  wrapper.appendChild(message);
  container.appendChild(wrapper);
}
